
import React, { useState, useEffect, useCallback, useMemo } from 'react';
const API_BASE = process.env.REACT_APP_API_BASE || '';
import { Testimonials, FAQ, CTA, Case, Contact, PleaseLogin, Register, Login, Forgot, Dashboard, KeywordsMini, NewReq, MyReqs, Admin, Denied, Field, Cart, TrustSection, CheckoutNotice } from './components';
import dataPersistence from './dataPersistence';
import analytics from './analytics';

// YouTube Icon Component
function YouTubeIcon({ className = "", size = 24 }) {
  return (
    <svg 
      className={className}
      width={size} 
      height={size} 
      viewBox="0 0 24 24" 
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
    </svg>
  );
}

// Icon Renderer Helper
function renderIcon(icon, className = "", size = 24) {
  if (icon === "youtube" || icon === "📺") {
    return <YouTubeIcon className={className || `w-${size} h-${size}`} size={size} />;
  }
  return <span className={className}>{icon}</span>;
}

// Omatech Digital — Canvas Preview with Service Slideshow
// Notes:
// - Fixed: React import + removed stray broken strings + cleaned Footer markup.
// - Fixed: UI test strings ("Unterminated string constant" bug) & kept original test intent.
// - Added: a couple of extra smoke tests.
// - Added: Beautiful service slideshow in the right panel

export default function OmatechDigitalPreview(){
  // helpers
  const safeGet=(k,fb)=>{try{const v=localStorage.getItem(k);return v?JSON.parse(v):fb;}catch{return fb}};
  const safeSet=(k,v)=>{try{localStorage.setItem(k,JSON.stringify(v));}catch{}};
  const uid=()=>Math.random().toString(36).slice(2,10);
  const fmt=(ts)=>new Date(ts).toLocaleString();
  const BRAND={name:"Omatech Digital",tag:"Grow Your Brand with Data-Driven Marketing"};
  const STATUSES=["Submitted","In Review","Quoted","In Progress","Completed"];
  const PROJECT_TYPES=["Web Development","Increase Youtube Followers (Organic)","Google Ads","Keyword Research","SEO","Landing Page","Other"];

  function useSeed(){
    const [users,setUsers]=useState(()=>{
      const userData = dataPersistence.getData("users",[]);
      console.log('🔄 Loading users:', userData.length);
      return userData;
    });
    const [reqs,setReqs]=useState(()=>{
      const reqData = dataPersistence.getData("reqs",[]);
      console.log('🔄 Loading requests:', reqData.length);
      return reqData;
    });
    const [session,setSession]=useState(()=>{
      const sessionData = dataPersistence.getData("session",null);
      console.log('🔄 Loading session:', sessionData ? 'Active' : 'None');
      return sessionData;
    });
    
    // Use new persistence system with automatic backup
    useEffect(()=>{
      if (users.length > 0) {
        console.log('💾 Saving users:', users.length);
        dataPersistence.setData("users", users);
      }
    },[users]);
    
    useEffect(()=>{
      if (reqs.length > 0) {
        console.log('💾 Saving requests:', reqs.length);
        dataPersistence.setData("reqs", reqs);
      }
    },[reqs]);
    
    useEffect(()=>{
      if (session) {
        console.log('💾 Saving session for:', session.email);
        dataPersistence.setData("session", session);
      }
    },[session]);
    
    // seed admin if not exists
    useEffect(()=>{
      if(!users.some(u=>u.email==="admin@omatechdigital.com")){
        console.log('👑 Creating admin user...');
        setUsers(p=>[...p,{email:"admin@omatechdigital.com",name:"Admin",password:"admin123",role:"admin",phone:"+918779157670",phoneVerified:true}]);
      }
    },[]);
    
    return {users,setUsers,reqs,setReqs,session,setSession};
  }

  // Initialize route from URL or default to "home"
  const getInitialRoute = () => {
    const path = window.location.pathname;
    if (path === '/' || path === '') return 'home';
    // Remove leading slash and use as route
    const routeFromPath = path.substring(1).toLowerCase();
    // Validate route exists
    const validRoutes = ['home', 'services', 'pricing', 'contact', 'login', 'register', 'dashboard', 'cart', 'case', 'forgot', 'newreq', 'myreqs', 'checkout'];
    return validRoutes.includes(routeFromPath) ? routeFromPath : 'home';
  };

  const [route, setRoute] = useState(getInitialRoute);
  const [cart, setCart] = useState([]);

  const {users,setUsers,reqs,setReqs,session,setSession}=useSeed();
  
  // Enhanced setRoute that updates browser history
  const navigateToRoute = useCallback((newRoute) => {
    setRoute(newRoute);
    // Update URL without page reload
    const newPath = newRoute === 'home' ? '/' : `/${newRoute}`;
    window.history.pushState({ route: newRoute }, '', newPath);
  }, []);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = (event) => {
      const newRoute = event.state?.route || getInitialRoute();
      setRoute(newRoute);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Initialize history state on mount if not already set
    if (!window.history.state || !window.history.state.route) {
      window.history.replaceState({ route }, '', route === 'home' ? '/' : `/${route}`);
    }

    // Listen for popstate events (back/forward button)
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [route]); // Include route in deps to update history state when route changes
  
  // Memoize expensive calculations to prevent unnecessary re-renders
  const me = useMemo(() => users.find(u=>u.email===session?.email)||null, [users, session?.email]);
  const isAdmin = useMemo(() => me?.role==="admin", [me?.role]);

  // Smoke tests (console assertions)
  useEffect(() => {
    try {
      console.assert(typeof BRAND.name === 'string', 'BRAND.name should be string');
      console.assert(Array.isArray(STATUSES) && STATUSES.length > 0, 'STATUSES should be non-empty');
    } catch (e) {
      console.error('Smoke tests failed', e);
    }
  }, []);

  // Memoized callback functions to prevent unnecessary re-renders
  const reset = useCallback(() => {
    // Use new persistence system to clear data
    dataPersistence.clearAllData();
    location.reload();
  }, []);

  const handleLogout = useCallback(() => {
    setSession(null);
  }, [setSession]);

  const goToLogin = useCallback(() => {
    navigateToRoute("login");
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [navigateToRoute]);

  const goToDashboard = useCallback(() => {
    navigateToRoute("dashboard");
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [navigateToRoute]);

  const goToNewReq = useCallback(() => {
    navigateToRoute("newreq");
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [navigateToRoute]);

  const goToMyReqs = useCallback(() => {
    navigateToRoute("myreqs");
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [navigateToRoute]);

  const handleLoggedIn = useCallback((email) => {
    setSession({email});
    
    // Track user login
    const user = users.find(u => u.email === email);
    analytics.trackLogin(email, user?.role || 'user');
    
    navigateToRoute("dashboard");
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [setSession, navigateToRoute, users]);

  const handleNewReqSubmit = useCallback(async (data) => {
    const rec = {
      id: uid(),
      userEmail: session.email,
      createdAt: Date.now(),
      status: "Submitted",
      ...data
    };
    setReqs(p => [rec, ...p]);
    
    // Send admin notification for requirements form submission
    console.log('🚀 Attempting to send admin notification for requirements form...');
    try {
              const response = await fetch('/.netlify/functions/admin-notification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          notificationType: 'form_submission',
          userData: {
            type: 'Requirements Form',
            userEmail: data.contact.email,
            userName: data.contact.name,
            userPhone: data.contact.phone || 'Not provided',
            subject: `New ${data.projectType} Project`,
            message: data.details || 'No detailed requirements provided',
            requirements: data,
            submissionTime: new Date().toLocaleString()
          }
        })
      });
      
      console.log('📧 Response status:', response.status);
      const responseData = await response.json();
      console.log('📧 Response data:', responseData);
      
      if (response.ok) {
        console.log('✅ Admin notification sent successfully for requirements form submission');
      } else {
        console.error('❌ Admin notification failed:', responseData);
      }
        } catch (error) {
      console.error('❌ Admin notification failed with error:', error);
    }
    
    // Send confirmation email to user
    try {
      await fetch('/.netlify/functions/user-notification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          notificationType: 'requirement_confirmation',
          userData: {
            name: data.contact.name,
            email: data.contact.email,
            projectType: data.projectType,
            requirements: data
          }
        })
      });
      console.log('📧 Requirement confirmation email sent to user');
    } catch (error) {
      console.warn('User requirement confirmation email failed:', error);
      // Don't fail the submission if user email fails
    }
    
    navigateToRoute("dashboard");
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [session?.email, setReqs, navigateToRoute]);

  const handleNewReqCancel = useCallback(() => {
    navigateToRoute("dashboard");
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [navigateToRoute]);

  // Cart functions
  const addToCart = useCallback((item) => {
    setCart(prev => [...prev, { ...item, id: uid(), addedAt: Date.now() }]);
  }, []);

  const removeFromCart = useCallback((itemId) => {
    setCart(prev => prev.filter(item => item.id !== itemId));
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
  }, []);

  const getCartTotal = useCallback(() => {
    const subtotal = cart.reduce((sum, item) => sum + item.price, 0);
    const gst = subtotal * 0.18; // 18% GST
    const total = subtotal + gst;
    return { subtotal, gst, total };
  }, [cart]);

  // Memoize filtered requirements to prevent unnecessary recalculations
  const userReqs = useMemo(() => 
    reqs.filter(r => r.userEmail === session?.email), 
    [reqs, session?.email]
  );

  // Scroll to top when route changes and track analytics
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Initialize Analytics
    analytics.init();
    
    // Track page view
    analytics.trackPageView(
      route === 'home' ? 'Home' : 
      route === 'services' ? 'Services' :
      route === 'pricing' ? 'Pricing' :
      route === 'contact' ? 'Contact' :
      route === 'login' ? 'Login' :
      route === 'register' ? 'Register' :
      route === 'dashboard' ? 'Dashboard' :
      route === 'cart' ? 'Cart' :
      route === 'case' ? 'Case Studies' :
      'Other',
      `/${route}`
    );
  }, [route]);



  return (
    <div className="min-h-screen text-gray-800 overflow-x-hidden">
      <Header route={route} setRoute={navigateToRoute} session={session} onLogout={handleLogout} onReset={reset} isAdmin={isAdmin} cartCount={cart.length}/>

      {/* Spacer to prevent content from being hidden under fixed header */}
      <div className="h-12 sm:h-14 md:h-16"></div>

      {route==="home" && <Home setRoute={navigateToRoute}/>}      
      {route==="services" && <Services setRoute={navigateToRoute} session={session} user={me}/>}
      {route==="pricing" && <Pricing setRoute={navigateToRoute} reqs={reqs} setReqs={setReqs} session={session} addToCart={addToCart}/>}
      {route==="cart" && <Cart cart={cart} removeFromCart={removeFromCart} clearCart={clearCart} getCartTotal={getCartTotal} setRoute={navigateToRoute} session={session}/>}
      {route==="case" && <Case/>}

      {route==="contact" && <Contact/>}

      {route==="register" && <Register users={users} setUsers={setUsers} goLogin={goToLogin}/>} 
      {route==="login" && <Login users={users} onLogged={handleLoggedIn} goForgotPassword={()=>navigateToRoute("forgot")} goSignUp={()=>navigateToRoute("register")}/>} 
      {route==="forgot" && <Forgot users={users} setUsers={setUsers} goLogin={goToLogin}/>} 

      {route==="dashboard" && (session? <Dashboard me={me} myReqs={userReqs} newReq={goToNewReq} goMy={goToMyReqs}/> : <PleaseLogin goLogin={goToLogin}/>)}
      {route==="newreq" && (session? <NewReq onSubmit={handleNewReqSubmit} onCancel={handleNewReqCancel}/> : <PleaseLogin goLogin={goToLogin}/>)}
      {route==="myreqs" && (session? <MyReqs items={userReqs}/> : <PleaseLogin goLogin={goToLogin}/>)}
      {route==="admin" && (isAdmin? <Admin items={reqs} setItems={setReqs} users={users} setUsers={setUsers}/> : <Denied/>) }

      {route==="checkout" && <CheckoutNotice session={session} setRoute={navigateToRoute}/>}

      {/* Floating WhatsApp button - visible on all pages, mobile-friendly */}
      <a
        href="https://wa.me/918779157670?text=Hi%20Omatech%20Digital,%20I%27d%20like%20a%20free%20consultation"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat on WhatsApp"
        className="fixed z-40 bottom-4 right-4 sm:bottom-6 sm:right-6"
      >
        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-[#25D366] shadow-lg shadow-black/30 flex items-center justify-center hover:scale-105 active:scale-95 transition-transform duration-200 touch-manipulation">
          <svg
            viewBox="0 0 32 32"
            className="w-7 h-7 sm:w-8 sm:h-8 text-white"
            aria-hidden="true"
          >
            <path
              fill="currentColor"
              d="M16.04 4C9.96 4 5 8.83 5 14.77c0 2.39.82 4.6 2.23 6.37L6 28l7.1-2.24c1.24.38 2.56.59 3.94.59 6.08 0 11.04-4.83 11.04-10.77C28.08 8.83 22.12 4 16.04 4zm0 18.93c-1.2 0-2.38-.2-3.48-.6l-.25-.09-4.21 1.32 1.37-4.02-.16-.26a7.8 7.8 0 01-1.2-4.21c0-4.31 3.55-7.81 7.93-7.81 4.36 0 7.9 3.5 7.9 7.81 0 4.31-3.54 7.86-7.9 7.86z"
            />
            <path
              fill="currentColor"
              d="M20.39 17.47c-.38-.19-2.21-1.09-2.55-1.21-.34-.13-.58-.19-.83.19-.25.38-.96 1.21-1.17 1.46-.21.25-.43.28-.8.09-.38-.19-1.6-.61-3.05-1.95-1.13-1.05-1.9-2.35-2.12-2.74-.21-.38-.02-.59.17-.77.17-.17.38-.45.57-.67.19-.21.25-.38.38-.64.13-.25.06-.47-.03-.66-.09-.19-.83-2-1.14-2.74-.3-.72-.6-.62-.83-.62h-.71c-.25 0-.66.09-1.01.47-.34.38-1.32 1.29-1.32 3.15 0 1.86 1.36 3.66 1.55 3.92.19.25 2.68 4.28 6.5 6 3.82 1.71 3.82 1.14 4.51 1.07.68-.06 2.21-.9 2.52-1.77.32-.87.32-1.61.22-1.77-.09-.17-.34-.27-.71-.45z"
            />
          </svg>
        </div>
      </a>

      <Footer setRoute={navigateToRoute}/>
    </div>
  );
}

// Service Slideshow Component
function ServiceSlideshow() {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const services = [
    {
      icon: "🌐",
      title: "Web Development",
      description: "Modern, responsive websites that convert visitors into customers",
      features: ["Mobile-first design", "SEO optimized", "Fast loading", "Conversion focused"],
      color: "from-blue-500 to-cyan-500",
      stats: "3.2x faster loading"
    },
    {
      icon: "youtube",
      title: "Increase Youtube Followers (Organic)",
      description: "Grow your YouTube channel with authentic, engaged subscribers through organic strategies",
      features: ["YouTube SEO", "Content strategy", "Community engagement", "Analytics tracking"],
      color: "from-red-500 to-pink-500",
      stats: "3x subscriber growth"
    },
    {
      icon: "📈",
      title: "Google Ads",
      description: "Full-funnel ad creation & management for maximum ROI",
      features: ["Search campaigns", "Display ads", "Remarketing", "Performance tracking"],
      color: "from-green-500 to-emerald-500",
      stats: "2.8x ROI improvement"
    },
    {
      icon: "🔍",
      title: "Keyword Research",
      description: "Best keywords for any business niche with intent mapping",
      features: ["Long-tail keywords", "Search volume", "Competition analysis", "Intent classification"],
      color: "from-orange-500 to-red-500",
      stats: "150+ keywords per niche"
    },
    {
      icon: "🚀",
      title: "SEO & On-Page",
      description: "Technical fixes and content mapping for search visibility",
      features: ["Technical SEO", "Content strategy", "Link building", "Local SEO"],
      color: "from-indigo-500 to-blue-500",
      stats: "45% traffic increase"
    },
    {
      icon: "🎯",
      title: "Landing Pages & CRO",
      description: "High-converting pages and A/B tests for better results",
      features: ["Conversion optimization", "A/B testing", "User experience", "Analytics setup"],
      color: "from-teal-500 to-green-500",
      stats: "67% conversion boost"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % services.length);
    }, 4000); // Change slide every 4 seconds

    return () => clearInterval(timer);
  }, [services.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % services.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + services.length) % services.length);
  };

  return (
    <div className="relative w-full h-full bg-white rounded-2xl border shadow-lg overflow-hidden">
      {/* Current Slide */}
      <div className="h-full flex flex-col justify-center items-center p-4 sm:p-6 md:p-8 text-center">
        {/* Icon with gradient background - responsive sizing */}
        <div className={`w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-2xl sm:rounded-3xl bg-gradient-to-br ${services[currentSlide].color} text-white grid place-items-center mb-4 sm:mb-6 shadow-2xl animate-pulse`}>
          {renderIcon(services[currentSlide].icon, '', services[currentSlide].icon === 'youtube' ? 48 : 0)}
        </div>
        
        {/* Title and description - responsive text sizes */}
        <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-3 sm:mb-4">{services[currentSlide].title}</h3>
        <p className="text-sm sm:text-base md:text-lg text-gray-600 mb-4 sm:mb-6 max-w-xs sm:max-w-sm md:max-w-md leading-relaxed px-2">{services[currentSlide].description}</p>
        
        {/* Stats badge - responsive sizing */}
        <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-gradient-to-r from-indigo-500 to-cyan-500 text-white text-xs sm:text-sm font-medium mb-4 sm:mb-6 shadow-lg">
          <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full animate-pulse"></span>
          {services[currentSlide].stats}
        </div>
      </div>

      {/* Navigation Dots - responsive positioning and sizing */}
      <div className="absolute bottom-2 sm:bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-1.5 sm:space-x-2">
        {services.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all duration-300 ${
              index === currentSlide 
                ? 'bg-indigo-600 scale-125' 
                : 'bg-gray-300 hover:bg-gray-400'
            }`}
          />
        ))}
      </div>

      {/* Navigation Arrows - responsive sizing and positioning */}
      <button
        onClick={prevSlide}
        className="absolute left-2 sm:left-4 top-1/2 transform -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-white/80 hover:bg-white rounded-full shadow-lg flex items-center justify-center text-gray-600 hover:text-indigo-600 transition-all duration-300 text-sm sm:text-base"
      >
        ←
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-2 sm:right-4 top-1/2 transform -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-white/80 hover:bg-white rounded-full shadow-lg flex items-center justify-center text-gray-600 hover:text-indigo-600 transition-all duration-300 text-sm sm:text-base"
      >
        →
      </button>
    </div>
  );
}

function Services({compact=false, setRoute, session, user}){
  const services = [
    {
      icon: "🎨",
      title: "Custom Webdesigning Service",
      subtitle: "Upload your design, we build it for you",
      description: "Have a design ready? Upload your design files (Figma, PSD, Sketch, or images) and we'll transform it into a fully functional, responsive website exactly as you envisioned.",
      features: [
        "Design File Upload Support",
        "Pixel-Perfect Implementation",
        "Responsive Development",
        "Cross-Browser Compatibility",
        "SEO Optimization",
        "Fast Loading Performance"
      ],
      benefits: [
        "Bring your vision to life",
        "Save time on development",
        "Professional implementation",
        "Fully responsive website"
      ],
      technologies: ["Figma", "Adobe XD", "PSD", "Sketch", "React", "Next.js"],
      timeline: "1-3 weeks",
      hasUpload: true
    },
    {
      icon: "🌐",
      title: "Web Development",
      subtitle: "Modern, responsive websites that convert",
      description: "Custom website development with modern frameworks, responsive design, SEO optimization, and performance tuning.",
      features: [
        "Responsive Design (Mobile-First)",
        "SEO-Optimized Architecture",
        "Fast Loading (< 3 seconds)",
        "Cross-Browser Compatibility",
        "Content Management System",
        "Analytics Integration"
      ],
      benefits: [
        "Increase online visibility",
        "Improve user engagement",
        "Boost conversion rates",
        "Enhance brand credibility"
      ],
      technologies: ["React", "Next.js", "Node.js", "WordPress", "Shopify"],
      timeline: "2-4 weeks"
    },
    {
      icon: "📺",
      title: "Increase Youtube Followers (Organic)",
      subtitle: "Grow your YouTube channel with authentic, engaged subscribers",
      description: "Comprehensive YouTube growth strategy using organic methods to increase subscribers, views, and engagement. We focus on content optimization, SEO, community building, and strategic promotion to build a loyal, active audience without paid ads.",
      features: [
        "YouTube SEO Optimization",
        "Content Strategy & Planning",
        "Thumbnail & Title Optimization",
        "Community Engagement",
        "Cross-Platform Promotion",
        "Analytics & Performance Tracking"
      ],
      benefits: [
        "Build authentic, engaged audience",
        "Increase organic reach and visibility",
        "Improve video rankings and discoverability",
        "Create sustainable long-term growth"
      ],
      technologies: ["YouTube Analytics", "SEO Tools", "Social Media", "Content Planning"],
      timeline: "3-6 months (ongoing)"
    },
    {
      icon: "📈",
      title: "Google Ads Management",
      subtitle: "Full-funnel ad creation & optimization",
      description: "Complete Google Ads management including keyword research, ad copy creation, landing page optimization, and performance tracking.",
      features: [
        "Keyword Research & Analysis",
        "Ad Copy Creation",
        "Landing Page Optimization",
        "Bid Management",
        "Performance Tracking",
        "A/B Testing"
      ],
      benefits: [
        "Generate qualified leads",
        "Increase brand awareness",
        "Improve ROI",
        "Target specific audiences"
      ],
      technologies: ["Google Ads", "Google Analytics", "Google Tag Manager"],
      timeline: "Ongoing"
    },
    {
      icon: "🔍",
      title: "Keyword Research",
      subtitle: "Data-driven keyword strategy",
      description: "Comprehensive keyword analysis using advanced tools to identify high-value, low-competition keywords for better rankings.",
      features: [
        "Search Volume Analysis",
        "Competition Research",
        "Long-tail Keywords",
        "Local SEO Keywords",
        "Seasonal Trends",
        "Keyword Clustering"
      ],
      benefits: [
        "Target high-intent keywords",
        "Reduce competition",
        "Improve search rankings",
        "Increase organic traffic"
      ],
      technologies: ["Ahrefs", "SEMrush", "Google Keyword Planner"],
      timeline: "1-2 weeks"
    },
    {
      icon: "🚀",
      title: "SEO & On-Page Optimization",
      subtitle: "Technical fixes and content strategy",
      description: "Technical SEO audits, on-page optimization, content strategy, and performance improvements to rank higher in search engines.",
      features: [
        "Technical SEO Audit",
        "On-Page Optimization",
        "Content Strategy",
        "Meta Tag Optimization",
        "Schema Markup",
        "Performance Optimization"
      ],
      benefits: [
        "Improve search rankings",
        "Increase organic traffic",
        "Enhance user experience",
        "Build authority"
      ],
      technologies: ["Google Search Console", "Screaming Frog", "Yoast SEO"],
      timeline: "2-3 weeks"
    },
    {
      icon: "🎯",
      title: "Landing Pages & CRO",
      subtitle: "High-converting pages and optimization",
      description: "Conversion-focused landing page design with A/B testing, user experience optimization, and analytics tracking.",
      features: [
        "Conversion-Focused Design",
        "A/B Testing",
        "User Experience Optimization",
        "Lead Capture Forms",
        "Analytics Tracking",
        "Heatmap Analysis"
      ],
      benefits: [
        "Increase conversion rates",
        "Reduce bounce rates",
        "Improve user engagement",
        "Maximize ROI"
      ],
      technologies: ["Hotjar", "Google Optimize", "Unbounce"],
      timeline: "1-3 weeks"
    }
  ];

  const whatsappUrl = (serviceName) =>
    `https://wa.me/918779157670?text=${encodeURIComponent(`Hi Omatech Digital, I'm interested in ${serviceName}. Please share more details.`)}`;

  const [selectedService, setSelectedService] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);

  const handleLearnMore = (service) => {
    if (compact) {
      setRoute && setRoute('services');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setSelectedService(service);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedService(null);
  };

  const handleDesignUpload = async (e, service) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    setUploadStatus(null);

    try {
      // Convert files to base64
      const filePromises = files.map(file => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const base64 = reader.result.split(',')[1]; // Remove data:image/...;base64, prefix
            resolve({
              name: file.name,
              content: base64,
              type: file.type,
              size: file.size
            });
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      });

      const fileData = await Promise.all(filePromises);

      // Send files to Netlify function as JSON
      const response = await fetch('/.netlify/functions/upload-design', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          files: fileData,
          service: service.title,
          timestamp: new Date().toISOString(),
          user: {
            email: session?.email || null,
            name: user?.name || '',
            phone: user?.phone || ''
          }
        })
      });

      const result = await response.json();

      if (response.ok) {
        setUploadStatus({
          type: 'success',
          message: `Design files uploaded successfully! We'll review and get back to you soon.`
        });
        // Reset file input
        e.target.value = '';
      } else {
        setUploadStatus({
          type: 'error',
          message: result.error || 'Failed to upload files. Please try again.'
        });
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus({
        type: 'error',
        message: 'An error occurred while uploading. Please try again.'
      });
    } finally {
      setUploading(false);
    }
  };

  // Compact (Home) rendering: hide key features, show minimal cards + link
  if (compact) {
  return (
      <section className="py-8">
      <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold mb-4">Our Services</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-2xl border shadow-sm p-5 hover:shadow-lg transition">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-cyan-500 text-white text-xl grid place-items-center mb-3">
                  {renderIcon(service.icon, service.icon === 'youtube' ? 'w-6 h-6' : '', service.icon === 'youtube' ? 24 : 0)}
      </div>
                <div className="font-semibold text-gray-900">{service.title}</div>
                <div className="text-sm text-gray-600 mt-1">{service.subtitle}</div>
                <button
                  onClick={() => handleLearnMore(service)}
                  className="mt-4 text-sm text-indigo-600 hover:text-indigo-800"
                >
                  More details →
                </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

  return (
    <>
      {/* Hero Section with Elegant Background */}
      <section className="relative min-h-screen bg-gradient-to-br from-slate-50 via-white to-[#12C7FF]/5 py-12 md:py-16 overflow-x-hidden">
        {/* Elegant Subtle Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 opacity-[0.02]" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, rgb(15, 23, 42) 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
          <div className="hidden sm:block absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-[#8B00FF]/20 via-[#7350FF]/15 to-[#12C7FF]/20 rounded-full blur-3xl"></div>
          <div className="hidden sm:block absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-[#7350FF]/15 via-[#8B00FF]/10 to-[#12C7FF]/15 rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4">
          {/* Header Section */}
          <div className="text-center mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#8B00FF]/10 via-[#7350FF]/10 to-[#12C7FF]/10 border border-[#7350FF]/20 text-[#3A3E46] text-sm font-medium mb-6">
              <span className="w-2 h-2 bg-gradient-to-r from-[#8B00FF] to-[#12C7FF] rounded-full"></span>
              Premium Digital Services
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
              Digital Solutions That
              <span className="block text-slate-700 font-light mt-2 text-3xl md:text-4xl">
                Drive Results
              </span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              From stunning websites to powerful applications, we deliver comprehensive digital solutions 
              that help businesses grow, engage customers, and achieve their goals.
            </p>
          </div>

          {/* ⭐ Premium USP: Shopify Web Designing & Integration - Top of Services */}
          <div className="mb-12 md:mb-16">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-[#0f172a] to-slate-900 border border-[#8B00FF]/30 shadow-2xl shadow-[#8B00FF]/10">
              <div className="absolute inset-0 opacity-30" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(139,0,255,0.15) 1px, transparent 0)', backgroundSize: '32px 32px' }} />
              <div className="absolute top-0 right-0 w-96 h-96 bg-[#8B00FF]/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#12C7FF]/15 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
              <div className="relative z-10 px-6 py-10 md:px-12 md:py-14 lg:px-16 lg:py-16">
                <div className="flex flex-wrap items-center gap-2 mb-6">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#8B00FF]/30 text-[#c4b5fd] text-xs font-semibold uppercase tracking-wider border border-[#8B00FF]/40">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Unique Selling Proposition
                  </span>
                  <span className="text-white/60 text-sm font-medium">Premium offering</span>
                </div>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 tracking-tight">
                  Shopify Web Designing & Integration
                </h2>
                <p className="text-lg text-white/80 max-w-3xl mb-10">
                  Enterprise-grade Shopify store design and seamless integration. We build your website and connect it to your Shopify store with a real-time sync system—no manual updates, no duplication.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
                  {[
                    'Custom Personalized Shopify Templates',
                    'High-Conversion Store Design',
                    'Advanced Product Page Optimization',
                    'Speed Optimization',
                    'Mobile UX Engineering',
                    'Payment Gateway Integration'
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 rounded-xl bg-white/5 border border-white/10 px-4 py-3">
                      <span className="text-[#12C7FF] text-lg">✓</span>
                      <span className="text-white font-medium text-sm md:text-base">{item}</span>
                    </div>
                  ))}
                </div>
                <div className="rounded-2xl bg-gradient-to-r from-[#8B00FF]/20 via-[#7350FF]/15 to-[#12C7FF]/20 border border-[#8B00FF]/40 p-6 md:p-8">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-2xl">⭐</span>
                    <h3 className="text-xl md:text-2xl font-bold text-white">Special Feature – Smart Shopify Sync System</h3>
                  </div>
                  <p className="text-white/90 leading-relaxed mb-4">
                    We build your website and seamlessly connect it with your Shopify store. Whenever you add or update a product in Shopify, the changes automatically reflect on your website in real-time within seconds.
                  </p>
                  <ul className="space-y-2 text-white/80 text-sm md:text-base">
                    <li className="flex items-center gap-2"><span className="text-emerald-400">•</span> No manual updates.</li>
                    <li className="flex items-center gap-2"><span className="text-emerald-400">•</span> No duplication.</li>
                    <li className="flex items-center gap-2"><span className="text-emerald-400">•</span> Fully automated product synchronization system.</li>
                  </ul>
                </div>
                <div className="mt-8">
                  <a
                    href={`https://wa.me/918779157670?text=${encodeURIComponent('Hi Omatech Digital, I am interested in Shopify Web Designing & Integration. Please share more details and next steps.')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#25D366] text-white font-semibold shadow-lg hover:bg-[#20bd5a] transition-all duration-200 hover:scale-105"
                  >
                    Get quote on WhatsApp
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {services.map((service, index) => (
              <div 
                key={index} 
                className="group bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border border-slate-200 overflow-hidden transform hover:-translate-y-1"
              >
                {/* Service Header */}
                <div className="p-6 md:p-8">
                  <div className={`w-16 h-16 rounded-xl ${service.icon === 'youtube' ? 'bg-gradient-to-br from-red-500 to-pink-500' : 'bg-slate-100'} text-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 ${service.icon === 'youtube' ? 'text-white' : ''}`}>
                    {renderIcon(service.icon, service.icon === 'youtube' ? 'w-8 h-8' : '', service.icon === 'youtube' ? 32 : 24)}
                  </div>
                  
                  <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-2">
                    {service.title}
                  </h3>
                  
                  <p className="text-slate-700 mb-4 leading-relaxed">
                    {service.subtitle}
                  </p>
                  
                  <p className="text-sm text-slate-600 mb-6 leading-relaxed">
                    {service.description}
                  </p>

                  {/* Key Features */}
                  <div className="mb-6">
                    <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 bg-gradient-to-r from-[#8B00FF] to-[#12C7FF] rounded-full"></span>
                      Key Features
                    </h4>
                    <ul className="space-y-2">
                      {service.features.slice(0, 3).map((feature, idx) => (
                        <li key={idx} className="text-sm text-slate-600 flex items-center gap-2">
                          <span className="text-[#7350FF]">✓</span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Technologies */}
                  <div className="mb-6">
                    <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 bg-slate-600 rounded-full"></span>
                      Technologies
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {service.technologies.map((tech, idx) => (
                        <span key={idx} className="px-3 py-1 bg-slate-100 text-slate-700 text-xs rounded-full border border-slate-200">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Timeline */}
                  <div className="mb-6 text-sm text-slate-600">
                    <span className="font-semibold">Timeline:</span> {service.timeline}
                  </div>

                  {/* CTA Button */}
                  {service.hasUpload ? (
                    session ? (
                      <div className="space-y-3">
                        <label className="w-full py-3 px-6 text-white font-semibold rounded-lg transition-all duration-200 shadow-sm hover:shadow-md bg-gradient-to-r from-[#8B00FF] via-[#7350FF] to-[#12C7FF] hover:from-[#7A00E6] hover:via-[#6A40E6] hover:to-[#10B8E6] cursor-pointer flex items-center justify-center gap-2">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          Upload Your Design
                          <input
                            type="file"
                            multiple
                            accept=".fig,.psd,.sketch,.xd,.png,.jpg,.jpeg,.pdf,.zip"
                            className="hidden"
                            onChange={(e) => handleDesignUpload(e, service)}
                            disabled={uploading}
                          />
                        </label>
                        {uploadStatus && (
                          <div className={`text-sm text-center p-2 rounded-lg ${
                            uploadStatus.type === 'success' ? 'bg-green-50 text-green-700' : 
                            uploadStatus.type === 'error' ? 'bg-red-50 text-red-700' : 
                            'bg-blue-50 text-blue-700'
                          }`}>
                            {uploadStatus.message}
                          </div>
                        )}
                        {uploading && (
                          <div className="text-sm text-center text-slate-600">
                            Uploading... Please wait
                          </div>
                        )}
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setRoute && setRoute('login');
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className="w-full py-3 px-6 text-white font-semibold rounded-lg transition-all duration-200 shadow-sm hover:shadow-md bg-gradient-to-r from-[#8B00FF] via-[#7350FF] to-[#12C7FF] hover:from-[#7A00E6] hover:via-[#6A40E6] hover:to-[#10B8E6]"
                      >
                        Login to Upload Your Design
                      </button>
                    )
                  ) : (
                  <a
                    href={whatsappUrl(service.title)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full py-3 px-6 text-white font-semibold rounded-lg transition-all duration-200 shadow-sm hover:shadow-md bg-gradient-to-r from-[#8B00FF] via-[#7350FF] to-[#12C7FF] hover:from-[#7A00E6] hover:via-[#6A40E6] hover:to-[#10B8E6] flex items-center justify-center gap-2 group"
                  >
                    Know More
                    <span className="inline-flex items-center gap-1 text-white/90">
                      <svg className="w-5 h-5 text-[#25D366]" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                      <svg className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                    </span>
                  </a>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Why Choose Us Section */}
          <div className="mt-16 md:mt-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Why Choose Omatech Digital?
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                We combine technical expertise with creative innovation to deliver solutions that exceed expectations.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  icon: "🎯",
                  title: "Results-Driven",
                  description: "We focus on measurable outcomes and ROI for your business.",
                  gradient: "from-purple-500 to-pink-500"
                },
                {
                  icon: "⚡",
                  title: "Fast Delivery",
                  description: "Quick turnaround times without compromising on quality.",
                  gradient: "from-pink-500 to-cyan-500"
                },
                {
                  icon: "🛡️",
                  title: "Reliable Support",
                  description: "Ongoing maintenance and support to keep your digital assets running smoothly.",
                  gradient: "from-cyan-500 to-purple-500"
                },
                {
                  icon: "💡",
                  title: "Innovation",
                  description: "Latest technologies and best practices to keep you ahead of the competition.",
                  gradient: "from-purple-500 to-green-500"
                }
              ].map((item, index) => (
                <div key={index} className="text-center p-6 group bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all duration-300">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-slate-100 text-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    {item.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.description}</p>
                </div>
              ))}
            </div>
        </div>
      </div>
    </section>

      {/* Service Detail Modal */}
      {showModal && selectedService && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-slate-200 shadow-2xl">
            <div className="p-6 md:p-8">
              {/* Modal Header */}
              <div className="flex justify-between items-start mb-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-xl bg-slate-100 text-2xl flex items-center justify-center">
                    {selectedService.icon}
                  </div>
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-slate-900">
                      {selectedService.title}
                    </h2>
                    <p className="text-slate-600">{selectedService.subtitle}</p>
                  </div>
                </div>
                <button 
                  onClick={closeModal}
                  className="text-slate-400 hover:text-slate-600 text-2xl hover:bg-slate-100 rounded-full w-8 h-8 flex items-center justify-center transition-colors duration-200"
                >
                  ×
                </button>
              </div>

              {/* Service Description */}
              <div className="mb-8">
                <p className="text-slate-700 leading-relaxed text-lg">
                  {selectedService.description}
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Features */}
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-4">Features & Capabilities</h3>
                  <ul className="space-y-3">
                    {selectedService.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <span className="text-[#7350FF] mt-1">✓</span>
                        <span className="text-slate-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Benefits */}
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-4">Business Benefits</h3>
                  <ul className="space-y-3">
                    {selectedService.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <span className="text-slate-600 mt-1">→</span>
                        <span className="text-slate-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Technologies */}
              <div className="mt-8">
                <h3 className="text-xl font-bold text-slate-900 mb-4">Technologies We Use</h3>
                <div className="flex flex-wrap gap-3">
                  {selectedService.technologies.map((tech, idx) => (
                    <span key={idx} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-full font-medium border border-slate-200">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              {/* Timeline */}
              <div className="mt-8 p-6 bg-slate-50 rounded-xl border border-slate-200">
                <h4 className="font-semibold text-slate-900 mb-2">Project Timeline</h4>
                <p className="text-slate-600">{selectedService.timeline}</p>
              </div>

              {/* CTA Buttons */}
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <a
                  href={whatsappUrl(selectedService.title)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeModal}
                  className="flex-1 py-3 px-6 text-white font-semibold rounded-lg transition-all duration-200 shadow-sm hover:shadow-md bg-[#25D366] hover:bg-[#20bd5a] flex items-center justify-center gap-2"
                >
                  Know More on WhatsApp
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                </a>
                <button 
                  onClick={() => {
                    closeModal();
                    setRoute("contact");
                  }}
                  className="flex-1 py-3 px-6 border-2 border-slate-300 text-slate-700 font-semibold rounded-lg hover:bg-slate-50 hover:border-slate-400 transition-all duration-200"
                >
                  Get Free Consultation
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Pricing({setRoute, reqs, setReqs, session, addToCart}){
  const [addedToCart, setAddedToCart] = useState({});
  const [currency, setCurrency] = useState('INR');
  const [exchangeRate, setExchangeRate] = useState(null);
  const [loadingRate, setLoadingRate] = useState(false);
  
  // Fetch live exchange rate
  const fetchExchangeRate = async () => {
    setLoadingRate(true);
    try {
      // Using exchangerate-api.com (free, no API key required)
      const response = await fetch('https://api.exchangerate-api.com/v4/latest/INR');
      const data = await response.json();
      if (data && data.rates && data.rates.USD) {
        setExchangeRate(data.rates.USD);
      }
    } catch (error) {
      console.error('Failed to fetch exchange rate:', error);
      // Fallback to approximate rate if API fails
      setExchangeRate(0.012); // Approximate 1 INR = 0.012 USD
    } finally {
      setLoadingRate(false);
    }
  };

  // Fetch rate on mount
  useEffect(() => {
    fetchExchangeRate();
  }, []);

  // Convert price based on currency
  const convertPrice = (inrPrice) => {
    if (currency === 'INR') {
      return inrPrice;
    }
    if (exchangeRate) {
      return inrPrice * exchangeRate;
    }
    // Fallback calculation
    return inrPrice * 0.012;
  };

  // Format price with currency symbol
  const formatPrice = (inrPrice) => {
    const convertedPrice = convertPrice(inrPrice);
    if (currency === 'USD') {
      return `$${convertedPrice.toFixed(2)}`;
    }
    return `₹${inrPrice.toLocaleString('en-IN')}`;
  };

  // Toggle currency
  const toggleCurrency = () => {
    if (currency === 'INR') {
      if (!exchangeRate) {
        fetchExchangeRate();
      }
      setCurrency('USD');
    } else {
      setCurrency('INR');
    }
  };
  
  const tiers=[
    {n:"Starter",p:"₹14,999",price:14999,icon:"🎯",badge:"Best for small businesses",f:["1 landing page","Basic SEO","Keyword shortlist (25)","Ad setup"]},
    {n:"Growth",p:"₹39,999",price:39999,icon:"⚡",badge:"Most Popular",f:["Multi‑page site","Technical SEO","Keyword plan (100)","Ad mgmt (₹2L/mo spend)"]},
    {n:"Performance",p:"₹79,999",price:79999,icon:"🚀",badge:"For aggressive scaling",f:["Web app or advanced site","Content strategy","Keyword plan (250)","Ad mgmt (₹5L+/mo)"]},
  ];
  
  const handleAddToCart = (tier) => {
    const cartItem = {
      name: `${tier.n} Package`,
      description: tier.f.join(', '),
      price: tier.price,
      features: tier.f,
      packageType: tier.n
    };
    addToCart(cartItem);
    
    // Show visual feedback
    setAddedToCart(prev => ({ ...prev, [tier.n]: true }));
    
    // Reset after 2 seconds
    setTimeout(() => {
      setAddedToCart(prev => ({ ...prev, [tier.n]: false }));
    }, 2000);
  };
  
  return (
    <section className="relative min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 py-12 md:py-16 overflow-hidden">
      {/* Elegant Subtle Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgb(15, 23, 42) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}></div>
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-blue-100/40 to-indigo-100/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-slate-100/40 to-blue-50/30 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4">
        {/* Header Section */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-sm font-medium mb-6">
            <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
            Premium Pricing Plans
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
            Transparent Pricing
            <span className="block text-slate-700 font-light mt-2 text-3xl md:text-4xl">
              For Every Stage
            </span>
          </h1>
          <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed mb-6">
            Tell us your goals and we’ll recommend the right plan. Get a custom quote via WhatsApp.
          </p>
        </div>

        {/* Pricing Tiers */}
        <div className="grid md:grid-cols-3 gap-6 mb-10 hidden">
          {tiers.map((t,i)=>(
            <div key={i} className="rounded-2xl bg-white border shadow-sm p-6">
              <div className="text-sm text-gray-500">{t.n}</div>
              <div className="text-3xl font-bold my-2">{t.p}<span className="text-base font-normal text-gray-500">/setup</span></div>
              <ul className="space-y-2 text-sm text-gray-700">{t.f.map((f,j)=>(<li key={j}>✔ {f}</li>))}</ul>
              <button 
                onClick={() => handleAddToCart(t)}
                className={`mt-5 w-full py-2 rounded-lg text-white hover:shadow-lg transition-all duration-200 ${
                  addedToCart[t.n] ? 'bg-green-600 shadow-lg scale-105' : ''
                }`}
                style={addedToCart[t.n] ? {} : {backgroundImage:"linear-gradient(to right,#4f46e5,#06b6d4)"}}
                disabled={addedToCart[t.n]}
              >
                {addedToCart[t.n] ? '✅ Added to Cart!' : `Choose ${t.n}`}
              </button>
            </div>
          ))}
        </div>
        

        


        {/* Services – Get Quote */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold mb-6 text-slate-900">Our Services</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {icon:'🌐', name:'Web Development', desc:'Modern responsive websites', gradient:'from-blue-500 to-cyan-500'},
              {icon:'youtube', name:'Increase Youtube Followers (Organic)', desc:'Grow your YouTube channel organically', gradient:'from-red-500 to-pink-500'},
              {icon:'📈', name:'Google Ads Management (Monthly)', desc:'Full-funnel ads & optimization', gradient:'from-green-500 to-emerald-500'},
              {icon:'🔍', name:'Keyword Research', desc:'Data-driven keyword strategy', gradient:'from-orange-500 to-red-500'},
              {icon:'🚀', name:'SEO & On-Page Optimization', desc:'Technical & content SEO', gradient:'from-indigo-500 to-blue-500'},
              {icon:'🎯', name:'Landing Pages & CRO', desc:'High-converting landing pages', gradient:'from-teal-500 to-green-500'},
              {icon:'🎨', name:'Custom Webdesigning Service', desc:'Upload your design, we build it', gradient:'from-purple-500 to-indigo-500'},
            ].map((svc,i)=> (
              <div key={i} className="group bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-xl p-6 hover:-translate-y-1 transition-all duration-300">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-12 h-12 rounded-xl ${svc.icon === 'youtube' ? 'bg-gradient-to-br from-red-500 to-pink-500' : 'bg-slate-100'} grid place-items-center ${svc.icon === 'youtube' ? 'text-white' : 'text-xl'}`}>
                    {renderIcon(svc.icon, svc.icon === 'youtube' ? 'w-6 h-6' : '', svc.icon === 'youtube' ? 24 : 0)}
                  </div>
                  <div className="font-semibold text-slate-900 text-lg">{svc.name}</div>
                </div>
                <div className="text-sm text-slate-600 mb-4 leading-relaxed">{svc.desc}</div>
                <a
                  href={`https://wa.me/918779157670?text=${encodeURIComponent(`Hi Omatech Digital, I'm interested in ${svc.name}. Please share a custom quote.`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-3 rounded-lg text-white font-semibold transition-all duration-200 bg-[#25D366] hover:bg-[#20bd5a] shadow-sm hover:shadow-md flex items-center justify-center gap-2"
                >
                  Get quote on WhatsApp
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                </a>
              </div>
            ))}
          </div>
        </div>
        
        {/* Elegant CTA Section */}
        <div className="mt-20 text-center">
          <div className="max-w-4xl mx-auto">
            <div className="rounded-2xl bg-white border border-slate-200 p-8 shadow-xl">
              <h3 className="text-3xl font-bold text-slate-900 mb-4">Ready to Get Started?</h3>
              <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
                Choose your preferred service or contact us for a custom quote. Our team is ready to help you achieve your digital goals.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  onClick={() => setRoute("contact")}
                  className="px-8 py-4 bg-slate-900 text-white font-semibold rounded-lg hover:bg-slate-800 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  Get Free Consultation
                </button>
                <button 
                  onClick={() => setRoute("newreq")}
                  className="px-8 py-4 border-2 border-slate-300 text-slate-700 font-semibold rounded-lg hover:bg-slate-50 hover:border-slate-400 transition-all duration-200"
                >
                  Start New Project
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Logo Component - Using the provided SVG code
function OmatechLogo({ className = "", onClick }) {
  // Generate unique ID for gradient to avoid conflicts
  const gradientId = useMemo(() => `logo-gradient-${Math.random().toString(36).substr(2, 9)}`, []);
  
  return (
    <button 
      type="button"
      onClick={onClick}
      className={`flex items-center group transition-all duration-300 ${className}`}
    >
      {/* Icon - Gradient O ring with notch - Responsive sizing */}
      <svg 
        width="40"
        height="40"
        viewBox="0 0 200 200"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="group-hover:scale-105 transition-transform duration-300 flex-shrink-0 sm:w-[50px] sm:h-[50px]"
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="1" x2="1" y2="0">
            <stop offset="0" stopColor="#00A3FF" />
            <stop offset="1" stopColor="#7b1fa2" />
          </linearGradient>
        </defs>
        
        {/* Gradient ring */}
        <g fill="none" stroke={`url(#${gradientId})`} strokeWidth="20" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="100" cy="100" r="64" />
        </g>

        {/* Notch */}
        <rect
          x="124"
          y="70"
          width="12"
          height="30"
          rx="3"
          fill="white"
          opacity="0.35"
          transform="rotate(-18 130 85)"
        />
      </svg>
      
      {/* Text - Stacked vertically - Responsive sizing, icon and text almost touching */}
      <div className="flex flex-col ml-0 sm:ml-0.5">
        <span className="font-bold text-base sm:text-lg text-[#3A3E46] leading-tight">
          Omateach
        </span>
        <span className="text-[10px] sm:text-xs text-[#7F8792] font-medium uppercase tracking-wide">
          DIGITAL
        </span>
      </div>
    </button>
  );
}

function Header({route,setRoute,session,onLogout,onReset,isAdmin,cartCount}){
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showDataMenu, setShowDataMenu] = useState(false);
  
  const Nav=({to,children})=> <button onClick={()=>{
    setRoute(to);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }} className={"px-4 py-2 rounded-lg text-slate-700 hover:text-slate-900 hover:bg-slate-50 transition-all duration-200 text-sm font-medium "+(route===to?"text-slate-900 bg-slate-100 font-semibold":"")}>{children}</button>;
  
  const closeMobileMenu = () => setMobileMenuOpen(false);
  
  const handleExportData = () => {
    dataPersistence.exportData();
  };
  
  const handleImportData = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            dataPersistence.importData(e.target.result);
            alert('Data imported successfully! Please refresh the page.');
            location.reload();
          } catch (error) {
            alert('Failed to import data. Please check the file format.');
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  };
  
  const getDataStats = () => {
    return dataPersistence.getDataStats();
  };
  
  const forceDataMigration = () => {
    dataPersistence.forceMigration();
    alert('Data migration completed! Please refresh the page.');
    location.reload();
  };
  
  // Close data menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDataMenu && !event.target.closest('.data-menu-container')) {
        setShowDataMenu(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDataMenu]);
  
  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm overflow-x-hidden">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-1 sm:py-0.5 flex items-center justify-between w-full">
        <OmatechLogo 
          onClick={() => {
          setRoute('home');
          window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        />
        
        {/* Desktop Navigation */}
        <nav className="hidden md:flex flex-wrap items-center gap-1 text-sm">
          <Nav to="home">Home</Nav>
          <Nav to="services">Services</Nav>
          <Nav to="pricing">Pricing</Nav>
          <Nav to="contact">Contact</Nav>
          <button 
            onClick={() => setRoute('cart')} 
            className="px-4 py-2 rounded-lg text-slate-700 hover:text-slate-900 hover:bg-slate-50 transition-all duration-200 relative group"
          >
            <span className="flex items-center gap-2 text-sm font-medium">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <span className="hidden sm:inline">Cart</span>
            </span>
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-gradient-to-r from-[#8B00FF] to-[#12C7FF] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-semibold">
                {cartCount}
              </span>
            )}
          </button>
          {session && <Nav to="dashboard">Dashboard</Nav>}
          {session && <Nav to="myreqs">My Requests</Nav>}
          {session && isAdmin && <Nav to="admin">Admin</Nav>}
          
          {/* Data Management Dropdown - Only visible to admin */}
          {session && isAdmin && (
            <div className="relative data-menu-container">
            <button 
              onClick={() => setShowDataMenu(!showDataMenu)}
              className="px-4 py-2 rounded-xl text-white/80 hover:text-white hover:bg-white/10 transition-all duration-300 text-sm flex items-center gap-2"
            >
              <span className="text-lg">💾</span>
              <span>Data</span>
              <span className="text-xs transition-transform duration-300" style={{transform: showDataMenu ? 'rotate(180deg)' : 'rotate(0deg)'}}>▼</span>
            </button>
            
            {showDataMenu && (
              <div className="absolute right-0 mt-2 w-64 bg-slate-800/95 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl shadow-purple-500/25 z-50 p-4 space-y-3">
                <div className="text-xs text-white/60 border-b border-white/20 pb-3">
                  <strong className="text-white">Data Management</strong>
                  <div className="text-xs mt-1">
                    Users: {getDataStats()?.users || 0} | 
                    Requests: {getDataStats()?.reqs || 0}
                  </div>
                </div>
                
                <button 
                  onClick={handleExportData}
                  className="w-full text-left px-3 py-2 rounded-xl hover:bg-white/10 text-white/80 hover:text-white transition-all duration-300 text-sm flex items-center gap-2"
                >
                  📤 Export Data
                </button>
                
                <button 
                  onClick={handleImportData}
                  className="w-full text-left px-3 py-2 rounded-xl hover:bg-white/10 text-white/80 hover:text-white transition-all duration-300 text-sm flex items-center gap-2"
                >
                  📥 Import Data
                </button>
                
                <button 
                  onClick={() => {
                    if (confirm('This will restore your data from the latest backup. Continue?')) {
                      dataPersistence.restoreFromBackup();
                      alert('Backup restored! Please refresh the page.');
                      location.reload();
                    }
                  }}
                  className="w-full text-left px-3 py-2 rounded-xl hover:bg-white/10 text-white/80 hover:text-white transition-all duration-300 text-sm flex items-center gap-2"
                >
                  🔄 Restore Backup
                </button>
                
                <button 
                  onClick={() => {
                    if (confirm('This will force migration of any old data. Continue?')) {
                      forceDataMigration();
                    }
                  }}
                  className="w-full text-left px-3 py-2 rounded-xl hover:bg-white/10 text-white/80 hover:text-white transition-all duration-300 text-sm flex items-center gap-2"
                >
                  🔄 Force Migration
                </button>
                
                <div className="border-t border-white/20 pt-3">
                  <button 
                    onClick={() => {
                      if (confirm('⚠️ This will permanently delete ALL data. Are you sure?')) {
                        onReset();
                      }
                    }}
                    className="w-full text-left px-3 py-2 rounded-xl hover:bg-red-500/20 text-red-400 hover:text-red-300 transition-all duration-300 text-sm flex items-center gap-2"
                  >
                    🗑️ Clear All Data
                  </button>
                </div>
              </div>
            )}
          </div>
          )}
          
          {session? (
            <>
              <button onClick={onLogout} className="px-4 py-2 rounded-lg text-slate-700 hover:text-slate-900 hover:bg-slate-50 transition-all duration-200 text-sm font-medium">Logout</button>
            </>
          ):(
            <>
              <button onClick={()=>{
                setRoute("login");
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }} className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:text-slate-900 hover:bg-slate-50 transition-all duration-200 text-sm font-medium">Login</button>
              <button onClick={()=>{
                setRoute("register");
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }} className="px-4 py-2 rounded-lg text-white transition-all duration-200 text-sm font-semibold shadow-sm hover:shadow-md bg-gradient-to-r from-[#8B00FF] via-[#7350FF] to-[#12C7FF] hover:from-[#7A00E6] hover:via-[#6A40E6] hover:to-[#10B8E6]">Sign up</button>
            </>
          )}
        </nav>
        
        {/* Mobile Menu Button - Touch-friendly */}
        <div className="md:hidden flex items-center gap-2">
          <button 
            onClick={() => setRoute('cart')} 
            className="p-2.5 sm:p-3 rounded-lg text-slate-700 active:text-slate-900 active:bg-slate-50 hover:bg-slate-50 transition-all duration-200 relative touch-manipulation min-w-[44px] min-h-[44px] flex items-center justify-center"
            aria-label="Shopping cart"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-gradient-to-r from-[#8B00FF] to-[#12C7FF] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-semibold text-[10px]">
                {cartCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2.5 sm:p-3 rounded-lg text-slate-700 active:text-slate-900 active:bg-slate-50 hover:bg-slate-50 transition-all duration-200 touch-manipulation min-w-[44px] min-h-[44px] flex items-center justify-center text-xl sm:text-2xl"
            aria-label="Toggle menu"
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? '✕' : '☰'}
          </button>
      </div>
      </div>
      
      {/* Mobile Navigation Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-slate-200 bg-white max-h-[calc(100vh-80px)] overflow-y-auto">
          <nav className="px-4 py-3 space-y-1">
                            <button onClick={() => {
                  setRoute('home'); 
                  closeMobileMenu();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }} className="w-full text-left px-4 py-3.5 rounded-lg active:bg-slate-50 hover:bg-slate-50 text-slate-700 active:text-slate-900 hover:text-slate-900 transition-all duration-200 text-base font-medium touch-manipulation min-h-[44px] flex items-center">Home</button>
                          <button onClick={() => {
                setRoute('services'); 
                closeMobileMenu();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }} className="w-full text-left px-4 py-3.5 rounded-lg active:bg-slate-50 hover:bg-slate-50 text-slate-700 active:text-slate-900 hover:text-slate-900 transition-all duration-200 text-base font-medium touch-manipulation min-h-[44px] flex items-center">Services</button>
              <button onClick={() => {
                setRoute('pricing'); 
                closeMobileMenu();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }} className="w-full text-left px-4 py-3.5 rounded-lg active:bg-slate-50 hover:bg-slate-50 text-slate-700 active:text-slate-900 hover:text-slate-900 transition-all duration-200 text-base font-medium touch-manipulation min-h-[44px] flex items-center">Pricing</button>

              <button onClick={() => {
                setRoute('contact'); 
                closeMobileMenu();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }} className="w-full text-left px-4 py-3.5 rounded-lg active:bg-slate-50 hover:bg-slate-50 text-slate-700 active:text-slate-900 hover:text-slate-900 transition-all duration-200 text-base font-medium touch-manipulation min-h-[44px] flex items-center">Contact</button>
                                                      {session && <button onClick={() => {
                  setRoute('dashboard'); 
                  closeMobileMenu();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }} className="w-full text-left px-4 py-3.5 rounded-lg active:bg-slate-50 hover:bg-slate-50 text-slate-700 active:text-slate-900 hover:text-slate-900 transition-all duration-200 text-base font-medium touch-manipulation min-h-[44px] flex items-center">Dashboard</button>}
              {session && <button onClick={() => {
                setRoute('myreqs'); 
                closeMobileMenu();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }} className="w-full text-left px-4 py-3.5 rounded-lg active:bg-slate-50 hover:bg-slate-50 text-slate-700 active:text-slate-900 hover:text-slate-900 transition-all duration-200 text-base font-medium touch-manipulation min-h-[44px] flex items-center">My Requests</button>}
              {session && isAdmin && <button onClick={() => {
                setRoute('admin'); 
                closeMobileMenu();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }} className="w-full text-left px-4 py-3.5 rounded-lg active:bg-slate-50 hover:bg-slate-50 text-slate-700 active:text-slate-900 hover:text-slate-900 transition-all duration-200 text-base font-medium touch-manipulation min-h-[44px] flex items-center">Admin</button>}
                          {session ? (
                <button onClick={() => {onLogout(); closeMobileMenu();}} className="w-full text-left px-4 py-3.5 rounded-lg active:bg-red-50 hover:bg-red-50 text-red-600 active:text-red-700 hover:text-red-700 transition-all duration-200 text-base font-medium touch-manipulation min-h-[44px] flex items-center">Logout</button>
              ) : (
                <div className="flex gap-3 pt-3">
                  <button onClick={() => {setRoute("login"); closeMobileMenu();}} className="flex-1 px-4 py-3 rounded-lg border-2 border-slate-300 text-slate-700 active:text-slate-900 active:bg-slate-50 hover:bg-slate-50 transition-all duration-200 text-base font-medium touch-manipulation min-h-[44px]">Login</button>
                  <button onClick={() => {setRoute("register"); closeMobileMenu();}} className="flex-1 px-4 py-3 rounded-lg text-white text-base transition-all duration-200 font-semibold bg-gradient-to-r from-[#8B00FF] via-[#7350FF] to-[#12C7FF] active:from-[#7A00E6] active:via-[#6A40E6] active:to-[#10B8E6] hover:from-[#7A00E6] hover:via-[#6A40E6] hover:to-[#10B8E6] touch-manipulation min-h-[44px] active:scale-95">Sign up</button>
                </div>
              )}
          </nav>
        </div>
      )}
    </header>
  );
}

function Footer({setRoute}){
  // UI tests for remaining pages
  const runTests=()=>{
    const out=[]; const check=(name,cond)=>out.push(`${cond?'✅':'❌'} ${name}`);
    // Test 1: Contact page + form fields exist
    setRoute('contact'); setTimeout(()=>{
      const nameField=document.querySelector('input[placeholder="Your name"]');
      const msgField=document.querySelector('textarea');
      check('Contact form fields present', !!nameField && !!msgField);
      // Test 2: Services page renders
      setRoute('services'); setTimeout(()=>{
        const h1=document.querySelector('h1');
        check('Services heading renders', !!h1 && /Digital Solutions/i.test(h1?.textContent||''));
        // Test 3: Pricing page renders
        setRoute('pricing'); setTimeout(()=>{
          const h1b=document.querySelector('h1');
          check('Pricing heading renders', !!h1b && /Transparent Pricing/i.test(h1b?.textContent||''));
          // Back home and report
          setRoute('home');
          console.log('UI TESTS\n'+out.join('\n'));
          alert(out.join('\n'));
        },80);
      },80);
    },80);
  };
  return (
    <footer className="mt-20 border-t border-slate-200 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 md:grid-cols-4 gap-8">
        <div>
          <div className="mb-4">
            <OmatechLogo 
              onClick={() => {
                setRoute('home');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }} 
            />
            </div>
          <p className="text-sm text-slate-600 leading-relaxed">Grow Your Brand with Data-Driven Marketing</p>
        </div>
        <div>
          <h4 className="font-semibold text-slate-900 mb-4">Company</h4>
          <ul className="space-y-2 text-sm">
            <li><button onClick={()=>setRoute('services')} className="text-slate-600 hover:text-slate-900 transition-colors">Services</button></li>
            <li><button onClick={()=>setRoute('pricing')} className="text-slate-600 hover:text-slate-900 transition-colors">Pricing</button></li>
            <li><button onClick={()=>setRoute('contact')} className="text-slate-600 hover:text-slate-900 transition-colors">Contact</button></li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold text-slate-900 mb-4">Legal</h4>
          <ul className="space-y-2 text-sm text-slate-600">
            <li className="hover:text-slate-900 cursor-pointer transition-colors">Privacy Policy</li>
            <li className="hover:text-slate-900 cursor-pointer transition-colors">Terms & Conditions</li>
            <li className="hover:text-slate-900 cursor-pointer transition-colors">Cookies</li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold text-slate-900 mb-4">Contact</h4>
          <ul className="space-y-2 text-sm text-slate-600">
            <li>hello@omatechdigital.com</li>
            <li>+91 8779157670</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center text-sm text-slate-500">
            © {new Date().getFullYear()} Omatech Digital. All rights reserved.
          </div>
        </div>
      </div>
    </footer>
  );
}

// Service Slideshow Component for Home Page
function ServiceSlideshowHome({setRoute}){
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  
  const services = [
    { 
      icon: '🌐', 
      title: 'Web Development', 
      description: 'Modern, responsive websites that convert visitors into customers',
      gradient: 'from-blue-500 to-cyan-500',
      iconBg: 'bg-gradient-to-br from-blue-500 to-cyan-500'
    },
    { 
      icon: '📈', 
      title: 'SEO & Marketing', 
      description: 'Data-driven strategies to boost your online visibility and rankings',
      gradient: 'from-green-500 to-emerald-500',
      iconBg: 'bg-gradient-to-br from-green-500 to-emerald-500'
    },
    { 
      icon: '🎯', 
      title: 'Google Ads', 
      description: 'Full-funnel ad creation & management for maximum ROI',
      gradient: 'from-indigo-500 to-purple-500',
      iconBg: 'bg-gradient-to-br from-indigo-500 to-purple-500'
    },
    { 
      icon: 'youtube', 
      title: 'Increase Youtube Followers (Organic)', 
      description: 'Grow your YouTube channel with authentic, engaged subscribers through organic strategies',
      gradient: 'from-red-500 to-pink-500',
      iconBg: 'bg-gradient-to-br from-red-500 to-pink-500'
    },
    { 
      icon: '🎨', 
      title: 'Custom Webdesigning Service', 
      description: 'Upload your design, we build it for you - Transform your design into a fully functional website',
      gradient: 'from-purple-500 to-indigo-500',
      iconBg: 'bg-gradient-to-br from-purple-500 to-indigo-500'
    }
  ];
  
  // Each service is on its own slide
  const cardsPerSlide = 1;
  const totalSlides = services.length;
  
  
  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying || totalSlides <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    }, 4000); // Change slide every 4 seconds
    
    return () => clearInterval(interval);
  }, [isAutoPlaying, totalSlides]);
  
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
    setIsAutoPlaying(false); // Pause auto-play when user manually navigates
  };
  
  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
    setIsAutoPlaying(false); // Pause auto-play when user manually navigates
  };
  
  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false); // Pause auto-play when user manually navigates
  };
  
  return (
    <div className="relative w-full max-w-full bg-gradient-to-br from-slate-50 via-white to-[#12C7FF]/5 rounded-md sm:rounded-lg md:rounded-xl lg:rounded-2xl xl:rounded-3xl border border-slate-200 p-1 sm:p-1.5 md:p-2 lg:p-4 xl:p-6 shadow-md sm:shadow-lg md:shadow-xl overflow-hidden">
      {/* Subtle decorative gradient orb - hidden on mobile for performance */}
      <div className="hidden sm:block absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-[#8B00FF]/10 via-[#7350FF]/8 to-[#12C7FF]/10 rounded-full blur-3xl pointer-events-none"></div>
      <div className="hidden sm:block absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-[#7350FF]/8 via-[#8B00FF]/6 to-[#12C7FF]/8 rounded-full blur-3xl pointer-events-none"></div>
        
      {/* Slideshow Container */}
      <div className="relative overflow-hidden rounded-md sm:rounded-lg md:rounded-xl w-full z-10">
        {/* Service Cards - Slideshow */}
        <div 
          className="flex transition-transform duration-500 ease-in-out"
              style={{
            transform: `translateX(-${currentSlide * 100}%)`
              }}
        >
          {services.map((service, slideIndex) => (
            <div
              key={slideIndex} 
              className="min-w-full flex-shrink-0 flex justify-center items-center px-0.5 sm:px-1 md:px-1.5 lg:px-2"
            >
              <button
                onClick={() => {
                  analytics.trackServiceCardClick(service.title);
                  setRoute('services');
                  setTimeout(() => window.scrollTo({top: 0, behavior: 'smooth'}), 100);
                }}
                className="group relative bg-white rounded-md sm:rounded-lg md:rounded-xl lg:rounded-2xl p-1.5 sm:p-2 md:p-2.5 lg:p-4 xl:p-6 border border-slate-200 hover:border-slate-300 hover:shadow-lg sm:hover:shadow-xl md:hover:shadow-2xl transition-all duration-500 hover:-translate-y-0.5 sm:hover:-translate-y-1 md:hover:-translate-y-2 text-center w-full max-w-[160px] sm:max-w-[200px] md:max-w-[240px] lg:max-w-sm xl:max-w-md overflow-visible touch-manipulation"
                style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
              >
                {/* Gradient accent line at top */}
                <div className={`absolute top-0 left-0 right-0 h-0.5 sm:h-1 bg-gradient-to-r ${service.gradient}`}></div>
          
                {/* Icon with gradient background - Much smaller on mobile */}
                <div className={`w-7 h-7 sm:w-9 sm:h-9 md:w-11 md:h-11 lg:w-14 lg:h-14 xl:w-18 xl:h-18 rounded-md sm:rounded-lg md:rounded-xl lg:rounded-2xl ${service.iconBg} text-white flex items-center justify-center mb-1 sm:mb-1.5 md:mb-2 lg:mb-3 mx-auto group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-sm sm:shadow-md md:shadow-lg`}>
                  {service.icon === 'youtube' ? (
                    <YouTubeIcon className="w-3.5 h-3.5 sm:w-4.5 sm:h-4.5 md:w-5.5 md:h-5.5 lg:w-7 lg:h-7 xl:w-9 xl:h-9" />
                  ) : (
                    <span className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl">{service.icon}</span>
                  )}
        </div>

                {/* Title - Much smaller on mobile, prevent truncation */}
                <h3 className="text-[10px] sm:text-xs md:text-sm lg:text-base xl:text-lg font-bold text-slate-900 mb-0.5 sm:mb-1 md:mb-1.5 lg:mb-2 leading-tight group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-[#8B00FF] group-hover:via-[#7350FF] group-hover:to-[#12C7FF] transition-all duration-300 break-words" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                  {service.title}
                </h3>
                
                {/* Description - Much smaller on mobile, prevent truncation with proper line-height */}
                <p className="text-[8px] sm:text-[9px] md:text-[10px] lg:text-xs xl:text-sm text-slate-600 leading-[1.3] sm:leading-[1.35] md:leading-[1.4] lg:leading-relaxed mb-1 sm:mb-1.5 md:mb-2 lg:mb-2.5 px-0 break-words" style={{ wordBreak: 'break-word', overflowWrap: 'break-word', hyphens: 'auto' }}>
                  {service.description}
                </p>
                
                {/* CTA with gradient - Much smaller on mobile */}
                <div className="inline-flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-2 md:px-2.5 lg:px-3 py-0.5 sm:py-1 md:py-1.5 rounded-full bg-gradient-to-r from-[#8B00FF]/10 via-[#7350FF]/10 to-[#12C7FF]/10 border border-[#7350FF]/20 text-[#7350FF] font-semibold text-[6px] sm:text-[7px] md:text-[8px] lg:text-[9px] xl:text-xs group-hover:from-[#8B00FF] group-hover:via-[#7350FF] group-hover:to-[#12C7FF] group-hover:text-white group-hover:border-transparent transition-all duration-300 whitespace-nowrap">
                  <span>Learn more</span>
                  <svg className="w-1 h-1 sm:w-1.5 sm:h-1.5 md:w-2 md:h-2 lg:w-2.5 lg:h-2.5 group-hover:translate-x-0.5 sm:group-hover:translate-x-1 transition-transform flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>

                {/* Decorative gradient orb on hover - hidden on mobile */}
                <div className={`hidden sm:block absolute -bottom-20 -right-20 w-40 h-40 bg-gradient-to-br ${service.gradient} rounded-full opacity-0 group-hover:opacity-10 blur-3xl transition-opacity duration-500`}></div>
              </button>
            </div>
          ))}
        </div>
        
        {/* Navigation Arrows - Much smaller on mobile */}
        {totalSlides > 1 && (
          <>
                <button 
              onClick={prevSlide}
              className="absolute left-0 sm:left-0.5 md:left-1 lg:left-3 top-1/2 -translate-y-1/2 w-6 h-6 sm:w-7 sm:h-7 md:w-9 md:h-9 lg:w-11 lg:h-11 rounded-full bg-white border-2 border-slate-200 shadow-sm sm:shadow-md md:shadow-lg flex items-center justify-center active:bg-gradient-to-r active:from-[#8B00FF] active:via-[#7350FF] active:to-[#12C7FF] active:border-transparent hover:bg-gradient-to-r hover:from-[#8B00FF] hover:via-[#7350FF] hover:to-[#12C7FF] hover:border-transparent hover:shadow-xl transition-all duration-300 active:scale-95 hover:scale-110 z-10 group touch-manipulation"
              aria-label="Previous slide"
                >
              <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-3.5 md:h-3.5 lg:w-4 lg:h-4 xl:w-5 xl:h-5 text-slate-700 group-hover:text-white group-active:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
              </svg>
                </button>
                <button 
              onClick={nextSlide}
              className="absolute right-0 sm:right-0.5 md:right-1 lg:right-3 top-1/2 -translate-y-1/2 w-6 h-6 sm:w-7 sm:h-7 md:w-9 md:h-9 lg:w-11 lg:h-11 rounded-full bg-white border-2 border-slate-200 shadow-sm sm:shadow-md md:shadow-lg flex items-center justify-center active:bg-gradient-to-r active:from-[#8B00FF] active:via-[#7350FF] active:to-[#12C7FF] active:border-transparent hover:bg-gradient-to-r hover:from-[#8B00FF] hover:via-[#7350FF] hover:to-[#12C7FF] hover:border-transparent hover:shadow-xl transition-all duration-300 active:scale-95 hover:scale-110 z-10 group touch-manipulation"
              aria-label="Next slide"
                >
              <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-3.5 md:h-3.5 lg:w-4 lg:h-4 xl:w-5 xl:h-5 text-slate-700 group-hover:text-white group-active:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
              </svg>
                </button>
          </>
        )}
              </div>

      {/* Dot Indicators - Much smaller on mobile */}
      {totalSlides > 1 && (
        <div className="flex justify-center gap-0.5 sm:gap-1 md:gap-1.5 mt-1 sm:mt-1.5 md:mt-2 lg:mt-3">
          {Array.from({ length: totalSlides }).map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-0.5 sm:h-1 md:h-1.5 rounded-full transition-all duration-300 touch-manipulation ${
                index === currentSlide 
                  ? 'bg-gradient-to-r from-[#8B00FF] to-[#12C7FF] w-3 sm:w-4 md:w-5 lg:w-7 xl:w-9' 
                  : 'bg-slate-300 hover:bg-slate-400 w-0.5 sm:w-1 md:w-1.5'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
                </div>
      )}
      
      {/* Elegant CTA at bottom - Much smaller on mobile */}
      <div className="mt-1 sm:mt-1.5 md:mt-2 lg:mt-3 pt-1 sm:pt-1.5 md:pt-2 lg:pt-3 border-t border-slate-200 text-center">
                            <button 
          onClick={() => setRoute('services')}
          className="text-slate-700 hover:text-slate-900 active:text-slate-900 font-medium text-[7px] sm:text-[8px] md:text-[9px] lg:text-xs xl:text-sm flex items-center justify-center gap-0.5 sm:gap-1 mx-auto transition-colors touch-manipulation py-0.5"
        >
          View All Services
          <svg className="w-1 h-1 sm:w-1.5 sm:h-1.5 md:w-2 md:h-2 lg:w-2.5 lg:h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
                                  </div>
                                </div>
  );
}

function Home({setRoute}){
  return (
    <main className="overflow-hidden bg-white">
      {/* Hero Section - Elegant & Professional */}
      <section className="relative min-h-[calc(100vh-3.5rem)] sm:min-h-screen bg-gradient-to-br from-slate-50 via-white to-[#12C7FF]/5 overflow-hidden">
        {/* Elegant Subtle Background Pattern */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Subtle geometric pattern */}
          <div className="absolute inset-0 opacity-[0.02]" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, rgb(15, 23, 42) 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
          
          {/* Elegant gradient orbs - very subtle - Hidden on mobile for performance */}
          <div className="hidden sm:block absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-br from-[#8B00FF]/20 via-[#7350FF]/15 to-[#12C7FF]/20 rounded-full blur-3xl"></div>
          <div className="hidden sm:block absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tr from-[#7350FF]/15 via-[#8B00FF]/10 to-[#12C7FF]/15 rounded-full blur-3xl"></div>
          
          {/* Subtle accent lines */}
          <div className="absolute top-1/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-slate-200/50 to-transparent"></div>
          <div className="absolute bottom-1/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-slate-200/50 to-transparent"></div>
                                </div>
                                
        <div className="relative z-10 max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8 lg:py-12 xl:py-16">
          <div className="grid lg:grid-cols-2 gap-3 sm:gap-4 md:gap-6 lg:gap-8 xl:gap-12 items-center">
            {/* Hero Content - Show first on mobile */}
            <div className="text-center lg:text-left space-y-2 sm:space-y-3 md:space-y-4 lg:space-y-6 order-1 lg:order-1">
              {/* Elegant Badge */}
              <div className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-gradient-to-r from-[#8B00FF]/10 via-[#7350FF]/10 to-[#12C7FF]/10 border border-[#7350FF]/20 text-[#3A3E46] text-[10px] sm:text-xs md:text-sm font-medium">
                <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-gradient-to-r from-[#8B00FF] to-[#12C7FF] rounded-full animate-pulse"></span>
                <span className="whitespace-nowrap">Trusted by 500+ Businesses Across India</span>
                              </div>
                          
              {/* Main Headline - Elegant Typography */}
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold leading-[1.15] sm:leading-[1.1] tracking-tight break-words">
                <span className="text-slate-900 block mb-1 sm:mb-2">
                  Transform Your Business with
                </span>
                <span className="bg-gradient-to-r from-[#8B00FF] via-[#7350FF] to-[#12C7FF] bg-clip-text text-transparent block break-words">
                  Digital Excellence
                </span>
              </h1>

              {/* Subtitle - Professional */}
              <p className="text-sm sm:text-base md:text-lg lg:text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto lg:mx-0 font-light px-1 sm:px-2 md:px-0 break-words">
                Leading digital marketing agency specializing in web development, SEO services, and Google Ads management. We help businesses achieve measurable growth with data-driven strategies.
              </p>

              {/* CTA Buttons - Elegant Design - Touch-friendly */}
              <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-3 md:gap-4 justify-center lg:justify-start pt-2 sm:pt-3 md:pt-4">
                <button 
                  onClick={() => {
                    setRoute("contact");
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }} 
                  className="group relative px-5 sm:px-6 md:px-8 py-2.5 sm:py-3 md:py-3.5 text-white text-xs sm:text-sm md:text-base font-semibold rounded-lg overflow-hidden transition-all duration-300 active:scale-95 hover:shadow-xl hover:-translate-y-0.5 bg-gradient-to-r from-[#8B00FF] via-[#7350FF] to-[#12C7FF] active:from-[#7A00E6] active:via-[#6A40E6] active:to-[#10B8E6] hover:from-[#7A00E6] hover:via-[#6A40E6] hover:to-[#10B8E6] touch-manipulation min-h-[44px]"
                >
                  <span className="relative z-10 flex items-center justify-center gap-1.5 sm:gap-2">
                    Get Free Consultation
                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </span>
                          </button>
                          <button 
                  onClick={()=>setRoute("services")} 
                  className="px-5 sm:px-6 md:px-8 py-2.5 sm:py-3 md:py-3.5 border-2 border-slate-300 text-slate-700 text-xs sm:text-sm md:text-base font-semibold rounded-lg active:scale-95 hover:border-slate-400 hover:bg-slate-50 active:bg-slate-50 transition-all duration-300 hover:-translate-y-0.5 touch-manipulation min-h-[44px]"
                >
                  Explore Services
                          </button>
                        </div>
                        
              {/* Trust Indicators - Elegant Stats */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 sm:gap-6 md:gap-8 pt-4 sm:pt-6 md:pt-8">
                <div className="text-center lg:text-left">
                  <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-0.5 sm:mb-1">500+</div>
                  <div className="text-slate-600 text-xs sm:text-sm font-medium">Happy Clients</div>
                          </div>
                <div className="hidden sm:block w-px h-10 sm:h-12 bg-slate-200"></div>
                <div className="text-center lg:text-left">
                  <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-0.5 sm:mb-1">98%</div>
                  <div className="text-slate-600 text-xs sm:text-sm font-medium">Success Rate</div>
                        </div>
                <div className="hidden sm:block w-px h-10 sm:h-12 bg-slate-200"></div>
                <div className="text-center lg:text-left">
                  <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-0.5 sm:mb-1">4.9★</div>
                  <div className="text-slate-600 text-xs sm:text-sm font-medium">Client Rating</div>
                      </div>
                    </div>
                  </div>

            {/* Hero Visual - Elegant Design - Show second on mobile, after hero content */}
            <div className="relative order-2 lg:order-2 mt-3 sm:mt-4 md:mt-6 lg:mt-0 mb-0 w-full">
              <div className="relative w-full max-w-full">
                {/* Elegant Service Showcase - Slideshow */}
                <ServiceSlideshowHome setRoute={setRoute} />
              </div>
            </div>
          </div>
        </div>

        {/* Elegant Scroll Indicator - Hidden on mobile */}
        <div className="hidden sm:flex absolute bottom-8 left-1/2 transform -translate-x-1/2">
          <div className="flex flex-col items-center gap-2">
            <span className="text-xs text-slate-500 font-medium">Scroll to explore</span>
            <div className="w-6 h-10 border-2 border-slate-300 rounded-full flex justify-center">
              <div className="w-1 h-3 bg-slate-400 rounded-full mt-2 animate-bounce"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview Section - Elegant Design */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-white overflow-x-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#8B00FF]/10 via-[#7350FF]/10 to-[#12C7FF]/10 border border-[#7350FF]/20 text-[#3A3E46] text-sm font-medium mb-6">
              <span className="w-2 h-2 bg-gradient-to-r from-[#8B00FF] to-[#12C7FF] rounded-full"></span>
              Our Core Services
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4 sm:mb-6 leading-tight px-2 sm:px-0">
              Comprehensive Digital Solutions
              <span className="block text-slate-700 font-light mt-2 text-xl sm:text-2xl lg:text-3xl">
                For Your Business Growth
              </span>
            </h2>
            <p className="text-base sm:text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed px-2 sm:px-0">
              Leading digital marketing agency specializing in web development, SEO services, Google Ads management, and e-commerce solutions. We deliver measurable results that drive business growth.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: "🌐",
                title: "Web Development Services India",
                description: "Professional web development company in India creating responsive, SEO-optimized websites that convert visitors into customers",
                gradient: "from-blue-500 to-cyan-500",
                features: ["Mobile-first responsive design", "SEO optimized development", "Fast loading websites", "Conversion-focused design"]
              },
              {
                icon: "⚡",
                title: "E-commerce Development India",
                description: "Top e-commerce development company in India building scalable online stores with modern technology and conversion optimization",
                gradient: "from-purple-500 to-pink-500",
                features: ["React/Next.js development", "Node.js backend services", "Database optimization", "API integration"]
              },
              {
                icon: "📈",
                title: "Google Ads Management India",
                description: "Expert Google Ads management services in India with full-funnel campaign creation and optimization for maximum ROI",
                gradient: "from-green-500 to-emerald-500",
                features: ["Search campaign optimization", "Display advertising", "Remarketing campaigns", "Performance analytics"]
              },
              {
                icon: "🔍",
                title: "SEO Services India",
                description: "Professional SEO services in India with comprehensive keyword research, on-page optimization, and search ranking improvements",
                gradient: "from-orange-500 to-red-500",
                features: ["Long-tail keyword research", "Search volume analysis", "Competition analysis", "Intent-based optimization"]
              },
              {
                icon: "🚀",
                title: "Digital Marketing Agency India",
                description: "Leading digital marketing agency in India providing comprehensive online marketing strategies and technical SEO solutions",
                gradient: "from-indigo-500 to-blue-500",
                features: ["Technical SEO optimization", "Content marketing strategy", "Link building services", "Local SEO for India"]
              },
              {
                icon: "🎯",
                title: "Conversion Rate Optimization India",
                description: "Expert conversion rate optimization services in India with high-converting landing pages and A/B testing for better business results",
                gradient: "from-teal-500 to-green-500",
                features: ["Landing page optimization", "A/B testing services", "User experience design", "Analytics setup"]
              }
            ].map((service, index) => (
              <button 
                key={index} 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log(`${service.title} card clicked`);
                  setRoute('services');
                  setTimeout(() => window.scrollTo({top: 0, behavior: 'smooth'}), 100);
                }}
                className="group bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-slate-200 hover:border-slate-300 overflow-hidden focus:outline-none focus:ring-2 focus:ring-blue-500/50 w-full text-left"
                aria-label={`Learn more about ${service.title}`}
                title={`Click to explore ${service.title} services`}
                type="button"
              >
                <div className="p-8">
                  <div className={`w-14 h-14 rounded-xl ${service.icon === 'youtube' ? 'bg-gradient-to-br from-red-500 to-pink-500' : 'bg-slate-100'} text-2xl flex items-center justify-center mb-6 ${service.icon === 'youtube' ? '' : 'group-hover:bg-slate-200'} transition-colors duration-300 ${service.icon === 'youtube' ? 'text-white' : ''}`}>
                    {renderIcon(service.icon, service.icon === 'youtube' ? 'w-7 h-7' : '', service.icon === 'youtube' ? 28 : 0)}
                  </div>
                  
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    {service.title}
                  </h3>
                  
                  <p className="text-slate-600 mb-6 leading-relaxed">
                    {service.description}
                  </p>

                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                        <span className="w-1.5 h-1.5 bg-gradient-to-r from-[#8B00FF] to-[#12C7FF] rounded-full"></span>
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <div className="w-full py-3 px-6 text-white font-semibold rounded-lg transition-all duration-300 text-center bg-gradient-to-r from-[#8B00FF] via-[#7350FF] to-[#12C7FF] group-hover:from-[#7A00E6] group-hover:via-[#6A40E6] group-hover:to-[#10B8E6]">
                    Learn More →
                  </div>
                </div>
              </button>
              ))}
              </div>
            </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-slate-900">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Why Industry Leaders
              <span className="block bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                Choose Omatech Digital
              </span>
            </h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              We combine technical expertise with creative innovation to deliver solutions that exceed expectations
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: "🎯",
                title: "Results-Driven",
                description: "We focus on measurable outcomes and ROI for your business.",
                color: "from-purple-500 to-pink-500"
              },
              {
                icon: "⚡",
                title: "Fast Delivery",
                description: "Quick turnaround times without compromising on quality.",
                color: "from-blue-500 to-cyan-500"
              },
              {
                icon: "🛡️",
                title: "Reliable Support",
                description: "Ongoing maintenance and support to keep your digital assets running smoothly.",
                color: "from-green-500 to-emerald-500"
              },
              {
                icon: "💡",
                title: "Innovation",
                description: "Latest technologies and best practices to keep you ahead of the competition.",
                color: "from-orange-500 to-red-500"
              }
            ].map((item, index) => (
              <div key={index} className="text-center group">
                <div className={`w-20 h-20 mx-auto mb-6 rounded-3xl bg-gradient-to-br ${item.color} text-white text-3xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                  {item.icon}
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{item.title}</h3>
                  <p className="text-white/70 leading-relaxed">{item.description}</p>
                </div>
              ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Our Proven
              <span className="block bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                Process
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A systematic approach that ensures consistent quality and predictable results
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Discovery", description: "We analyze your business, goals, and target audience to create a strategic roadmap", icon: "🔍" },
              { step: "02", title: "Strategy", description: "Data-driven planning and competitive analysis to position you for success", icon: "📊" },
              { step: "03", title: "Execution", description: "Expert implementation with attention to detail and quality assurance", icon: "⚡" },
              { step: "04", title: "Optimization", description: "Continuous monitoring and improvement to maximize your ROI", icon: "📈" }
            ].map((phase, index) => (
              <div key={index} className="relative group">
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                  <div className="text-6xl font-bold text-purple-100 mb-4">{phase.step}</div>
                  <div className="text-4xl mb-4">{phase.icon}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{phase.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{phase.description}</p>
                </div>
                
                {/* Connection Line */}
                {index < 3 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-purple-200 to-pink-200 transform -translate-y-1/2"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-purple-600 to-pink-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Transform
            <span className="block">Your Business?</span>
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Join hundreds of successful businesses that have grown with Omatech Digital. 
            Let's discuss your project and create something extraordinary together.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={()=>setRoute("newreq")} 
              className="px-8 py-4 bg-white text-purple-600 font-semibold rounded-2xl hover:bg-gray-50 transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              Start Your Project
            </button>
            <button 
              onClick={()=>setRoute("contact")} 
              className="px-8 py-4 border-2 border-white/30 text-white font-semibold rounded-2xl backdrop-blur-sm hover:bg-white/10 transition-all duration-300"
            >
              Get Free Consultation
            </button>
          </div>
        </div>
      </section>

      {/* Keep existing sections but with enhanced styling */}
      <TrustSection/>
      <Testimonials/>
      <FAQ/>
      <CTA setRoute={setRoute}/>
    </main>
  );
}

const Stat=({label,value})=> (
  <div className="p-4 rounded-lg bg-white border border-slate-200 shadow-sm">
    <div className="text-2xl font-bold text-slate-900">{value}</div>
    <div className="text-xs text-slate-600 mt-1">{label}</div>
  </div>
);
